import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import DnerhsApi from "api/DnerhsApi";
import { trackPromise } from "react-promise-tracker";
import * as XLSX from "xlsx";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: "16px 24px",
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
    color: "#fff",
  },
  tableContainer: {
    maxHeight: 500,
    marginTop: 16,
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    "& th": {
      fontWeight: 700,
      fontSize: "0.85rem",
      color: "#333",
      padding: "12px 16px",
      borderBottom: "2px solid #ddd",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f9f9f9",
    },
  },
  totalCell: {
    backgroundColor: "#e3f2fd",
    fontWeight: 700,
    color: "#1976d2",
  },
}));

const MatrizHorariosModal = ({ open, onClose, tutorId, tutorNombre, tutorCedula }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [matrizData, setMatrizData] = useState([]);
  const [establecimientos, setEstablecimientos] = useState([]);
  const [horariosAgrupados, setHorariosAgrupados] = useState([]);
  const [totalesPorDiaHorario, setTotalesPorDiaHorario] = useState({});

  useEffect(() => {
    if (open && tutorId) {
      fetchMatrizHorarios();
    }
  }, [open, tutorId]);

  const fetchMatrizHorarios = async () => {
    setLoading(true);
    try {
      const { data } = await trackPromise(
        DnerhsApi.get(`/tutores/${tutorId}/matriz-horarios`)
      );
      setMatrizData(data);
      procesarDatos(data);
    } catch (error) {
      console.error("Error al obtener matriz de horarios:", error);
    } finally {
      setLoading(false);
    }
  };

  const normalizarDia = (dia) => {
    const diaStr = String(dia).toLowerCase().trim();
    const mapaDias = {
      '1': 'Lunes',
      'lunes': 'Lunes',
      '2': 'Martes',
      'martes': 'Martes',
      '3': 'Miércoles',
      'miercoles': 'Miércoles',
      'miércoles': 'Miércoles',
      '4': 'Jueves',
      'jueves': 'Jueves',
      '5': 'Viernes',
      'viernes': 'Viernes',
      '6': 'Sábado',
      'sabado': 'Sábado',
      'sábado': 'Sábado',
      '7': 'Domingo',
      'domingo': 'Domingo',
    };
    return mapaDias[diaStr] || dia;
  };

  const ordenDias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  const procesarDatos = (data) => {
    // Obtener lista única de establecimientos
    const establecimientosUnicos = [...new Set(data.map((item) => item.establecimientoNombre))];
    setEstablecimientos(establecimientosUnicos);

    // Agrupar por día, horario, carrera, curso, semestre y año
    const agrupado = {};
    data.forEach((item) => {
      const diaNormalizado = normalizarDia(item.dia);
      const key = `${diaNormalizado}|${item.horarioDesde}-${item.horarioHasta}|${item.carrera}|${item.curso}|${item.semestre}|${item.anio}`;
      if (!agrupado[key]) {
        agrupado[key] = {
          dia: diaNormalizado,
          horarioDesde: item.horarioDesde,
          horarioHasta: item.horarioHasta,
          carrera: item.carrera,
          curso: item.curso,
          semestre: item.semestre,
          anio: item.anio,
          establecimientos: {},
          total: 0,
        };
      }
      agrupado[key].establecimientos[item.establecimientoNombre] = item.cantidadAlumnos;
      agrupado[key].total += item.cantidadAlumnos;
    });

    // Ordenar por día de la semana, horario, carrera, curso, semestre y año
    const horariosOrdenados = Object.values(agrupado).sort((a, b) => {
      // 1. Ordenar por día de la semana
      const diaA = ordenDias.indexOf(a.dia);
      const diaB = ordenDias.indexOf(b.dia);
      if (diaA !== diaB) {
        return diaA - diaB;
      }

      // 2. Ordenar por horario de inicio
      const horarioDesdeCompare = a.horarioDesde.localeCompare(b.horarioDesde);
      if (horarioDesdeCompare !== 0) {
        return horarioDesdeCompare;
      }

      // 3. Ordenar por horario de fin
      const horarioHastaCompare = a.horarioHasta.localeCompare(b.horarioHasta);
      if (horarioHastaCompare !== 0) {
        return horarioHastaCompare;
      }

      // 4. Ordenar por carrera
      const carreraCompare = (a.carrera || '').localeCompare(b.carrera || '');
      if (carreraCompare !== 0) {
        return carreraCompare;
      }

      // 5. Ordenar por curso (String)
      const cursoCompare = (a.curso || '').localeCompare(b.curso || '');
      if (cursoCompare !== 0) {
        return cursoCompare;
      }

      // 6. Ordenar por semestre
      if (a.semestre !== b.semestre) {
        return (a.semestre || 0) - (b.semestre || 0);
      }

      // 7. Ordenar por año
      return (a.anio || 0) - (b.anio || 0);
    });

    setHorariosAgrupados(horariosOrdenados);

    // Calcular grupos de horarios solapados y sus totales
    const gruposSolapados = [];
    const procesados = new Set();

    horariosOrdenados.forEach((row, index) => {
      if (procesados.has(index)) return;

      // Crear un nuevo grupo con esta fila
      const grupo = {
        indices: [index],
        filas: [row],
        total: row.total,
        dia: row.dia,
      };

      // Buscar todas las filas que se solapan con este grupo
      horariosOrdenados.forEach((otherRow, otherIndex) => {
        if (otherIndex <= index || procesados.has(otherIndex)) return;
        if (row.dia !== otherRow.dia) return;

        // Verificar si se solapa con alguna fila del grupo
        const seSolapa = grupo.filas.some(filaGrupo =>
          horariosSeSuperponen(filaGrupo.horarioDesde, filaGrupo.horarioHasta, otherRow.horarioDesde, otherRow.horarioHasta)
        );

        if (seSolapa) {
          grupo.indices.push(otherIndex);
          grupo.filas.push(otherRow);
          grupo.total += otherRow.total;
          procesados.add(otherIndex);
        }
      });

      procesados.add(index);
      gruposSolapados.push(grupo);
    });

    // Crear mapa de totales por índice
    const totales = {};
    gruposSolapados.forEach(grupo => {
      grupo.indices.forEach(idx => {
        totales[idx] = {
          total: grupo.total,
          esPrimero: idx === grupo.indices[0],
          rowspan: grupo.indices.length,
        };
      });
    });

    setTotalesPorDiaHorario(totales);
  };

  // Función para verificar si dos horarios se superponen
  const horariosSeSuperponen = (inicio1, fin1, inicio2, fin2) => {
    // Convertir horarios a minutos para comparar
    const toMinutes = (time) => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const start1 = toMinutes(inicio1);
    const end1 = toMinutes(fin1);
    const start2 = toMinutes(inicio2);
    const end2 = toMinutes(fin2);

    // Dos horarios se superponen si:
    // - El inicio de uno está entre el inicio y fin del otro, O
    // - El fin de uno está entre el inicio y fin del otro, O
    // - Uno contiene completamente al otro
    return (start1 < end2 && end1 > start2);
  };

  const exportarAExcel = () => {
    const worksheetData = [];

    // Encabezado
    worksheetData.push([`Matriz de Horarios - ${tutorNombre} (CI: ${tutorCedula})`]);
    worksheetData.push([]);

    // Cabecera de la tabla
    const header = ["Día", "Horario", "Carrera", "Curso", "Semestre", "Año", ...establecimientos, "TOTAL (Por Grupo)", "TOTAL SIMULTÁNEO"];
    worksheetData.push(header);

    // Datos
    horariosAgrupados.forEach((row, index) => {
      const infoTotal = totalesPorDiaHorario[index];
      const totalSimultaneo = infoTotal ? infoTotal.total : row.total;

      const rowData = [
        row.dia,
        `${row.horarioDesde}-${row.horarioHasta}`,
        row.carrera || 'N/A',
        row.curso || '-',
        row.semestre || '-',
        row.anio || '-',
        ...establecimientos.map((est) => row.establecimientos[est] || 0),
        row.total,
        totalSimultaneo,
      ];
      worksheetData.push(rowData);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Matriz Horarios");
    XLSX.writeFile(workbook, `Matriz_Horarios_${tutorNombre.replace(/ /g, "_")}.xlsx`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <CalendarTodayIcon style={{ marginRight: 12 }} />
          <Typography variant="h6">
            Matriz de Horarios - {tutorNombre} (CI: {tutorCedula})
          </Typography>
        </Box>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>Día</TableCell>
                  <TableCell>Horario</TableCell>
                  <TableCell>Carrera</TableCell>
                  <TableCell align="center">Curso</TableCell>
                  <TableCell align="center">Semestre</TableCell>
                  <TableCell align="center">Año</TableCell>
                  {establecimientos.map((est, index) => (
                    <TableCell key={index} align="center">
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          {est}
                        </Typography>
                        <Typography variant="caption" style={{ fontSize: "0.7rem", color: "#666", fontStyle: "italic" }}>
                          (Cant. Alumnos)
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell align="center" className={classes.totalCell}>
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        TOTAL
                      </Typography>
                      <Typography variant="caption" style={{ fontSize: "0.7rem", fontStyle: "italic" }}>
                        (Por Grupo)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" style={{ backgroundColor: "#e8f5e9", fontWeight: 700 }}>
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 700, fontSize: "0.85rem", color: "#2e7d32" }}>
                        TOTAL SIMULTÁNEO
                      </Typography>
                      <Typography variant="caption" style={{ fontSize: "0.7rem", color: "#2e7d32", fontStyle: "italic" }}>
                        (Mismo Horario)
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {horariosAgrupados.map((row, index) => {
                  const infoTotal = totalesPorDiaHorario[index];
                  const totalSimultaneo = infoTotal ? infoTotal.total : row.total;
                  const esPrimero = infoTotal ? infoTotal.esPrimero : true;
                  const rowspan = infoTotal ? infoTotal.rowspan : 1;

                  return (
                    <TableRow key={index} className={classes.tableRow}>
                      <TableCell style={{ fontWeight: 600 }}>{row.dia}</TableCell>
                      <TableCell>{`${row.horarioDesde} - ${row.horarioHasta}`}</TableCell>
                      <TableCell>{row.carrera || 'N/A'}</TableCell>
                      <TableCell align="center">{row.curso || '-'}</TableCell>
                      <TableCell align="center">{row.semestre || '-'}</TableCell>
                      <TableCell align="center">{row.anio || '-'}</TableCell>
                      {establecimientos.map((est, estIndex) => (
                        <TableCell key={estIndex} align="center">
                          {row.establecimientos[est] || 0}
                        </TableCell>
                      ))}
                      <TableCell align="center" className={classes.totalCell}>
                        {row.total}
                      </TableCell>
                      {esPrimero && (
                        <TableCell
                          align="center"
                          rowSpan={rowspan}
                          style={{
                            backgroundColor: "#e8f5e9",
                            fontWeight: 700,
                            color: "#2e7d32",
                            fontSize: "1.1rem",
                            verticalAlign: "middle"
                          }}
                        >
                          {totalSimultaneo}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={exportarAExcel} color="primary" startIcon={<GetAppIcon />}>
          Exportar a Excel
        </Button>
        <Button onClick={onClose} color="default">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatrizHorariosModal;

